import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import AddIcon from '@mui/icons-material/Add';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import LinkIcon from '@mui/icons-material/Link';
import { outreachCrmService } from '../../services/api/outreachCrmService';
import NkySpinner from '../common/NkySpinner/NkySpinner';
import NkySnackbarAlert from '../common/NkySnackbarAlert/NkySnackbarAlert';
import AdminCrmProspectModal from './AdminCrmProspectModal';
import AdminCrmSettingsModal from './AdminCrmSettingsModal';
import AdminCrmLinkBusinessModal from './AdminCrmLinkBusinessModal';
import {
  CrmToolbar,
  CrmKpiRow,
  CrmKpiCard,
  CrmKpiValue,
  CrmKpiLabel,
  CrmKpiPercent,
  CrmTableWrap,
  CrmTable,
  CrmTableRow,
  CrmSortHeaderButton,
  CrmPrimaryButton,
  CrmSecondaryButton,
  CrmField,
  stageChipSx,
  stageKpiCardSx,
  totalKpiCardSx,
} from './AdminCrm.styled';

function labelFor(list, id) {
  if (!id) return '—';
  return list?.find((item) => item.id === id)?.label || id;
}

const SORT_COLUMNS = [
  { id: 'business', label: 'Business' },
  { id: 'trades', label: 'Trades' },
  { id: 'status', label: 'Status' },
  { id: 'rm', label: 'RM' },
  { id: 'phone', label: 'Phone' },
  { id: 'email', label: 'Email' },
  { id: 'lastOutreach', label: 'Last outreach' },
  { id: 'channel', label: 'Channel' },
];

function compareText(a, b) {
  return String(a || '').localeCompare(String(b || ''), undefined, {
    sensitivity: 'base',
    numeric: true,
  });
}

function AdminCrm({ businesses = [] }) {
  const [settings, setSettings] = useState(null);
  const [summary, setSummary] = useState(null);
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tradeFilter, setTradeFilter] = useState('');
  const [rmFilter, setRmFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('business');
  const [sortDir, setSortDir] = useState('asc');
  const [prospectModalOpen, setProspectModalOpen] = useState(false);
  const [editingProspect, setEditingProspect] = useState(null);
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [linkingProspect, setLinkingProspect] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({
    open: false,
    severity: 'success',
    message: '',
  });

  const showToast = (severity, message) => {
    setToast({ open: true, severity, message });
  };

  const filters = useMemo(
    () => ({
      ...(tradeFilter ? { tradeId: tradeFilter } : {}),
      ...(rmFilter ? { relationshipManagerId: rmFilter } : {}),
    }),
    [tradeFilter, rmFilter],
  );

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [settingsRes, summaryRes, prospectsRes] = await Promise.all([
        outreachCrmService.getSettings(),
        outreachCrmService.getSummary(filters),
        outreachCrmService.listProspects(filters),
      ]);
      setSettings(settingsRes);
      setSummary(summaryRes);
      setProspects(prospectsRes);
    } catch (e) {
      setError(e.message || 'Failed to load CRM data');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const stageLabelById = useMemo(() => {
    const map = new Map();
    (settings?.pipelineStages || []).forEach((s) => map.set(s.id, s.label));
    return map;
  }, [settings]);

  const stageOrderById = useMemo(() => {
    const map = new Map();
    (settings?.pipelineStages || []).forEach((s) =>
      map.set(s.id, typeof s.sortOrder === 'number' ? s.sortOrder : 999),
    );
    return map;
  }, [settings]);

  const handleSort = (columnId) => {
    if (sortBy === columnId) {
      setSortDir((dir) => (dir === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortBy(columnId);
    setSortDir('asc');
  };

  const handleStatusFilter = (statusId) => {
    setStatusFilter((current) => (current === statusId ? '' : statusId));
  };

  const filteredProspects = useMemo(() => {
    const q = search.trim().toLowerCase();
    let filtered = statusFilter
      ? prospects.filter((p) => p.statusId === statusFilter)
      : [...prospects];

    if (q) {
      filtered = filtered.filter((p) => {
        const haystack = [
          p.businessName,
          p.ownerContact,
          p.city,
          p.notes,
          p.phone,
          p.email,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return haystack.includes(q);
      });
    }
    const direction = sortDir === 'asc' ? 1 : -1;
    filtered.sort((a, b) => {
      let cmp = 0;
      switch (sortBy) {
        case 'business':
          cmp = compareText(
            a.businessName || a.ownerContact,
            b.businessName || b.ownerContact,
          );
          break;
        case 'trades':
          cmp = compareText(
            (a.tradeIds || [])
              .map((id) => labelFor(settings?.trades, id))
              .join(', '),
            (b.tradeIds || [])
              .map((id) => labelFor(settings?.trades, id))
              .join(', '),
          );
          break;
        case 'status':
          cmp =
            (stageOrderById.get(a.statusId) ?? 999) -
            (stageOrderById.get(b.statusId) ?? 999);
          if (cmp === 0) {
            cmp = compareText(
              stageLabelById.get(a.statusId),
              stageLabelById.get(b.statusId),
            );
          }
          break;
        case 'rm':
          cmp = compareText(
            labelFor(settings?.relationshipManagers, a.relationshipManagerId),
            labelFor(settings?.relationshipManagers, b.relationshipManagerId),
          );
          break;
        case 'phone':
          cmp = compareText(a.phone, b.phone);
          break;
        case 'email':
          cmp = compareText(a.email, b.email);
          break;
        case 'lastOutreach':
          cmp = compareText(a.lastOutreachDate, b.lastOutreachDate);
          break;
        case 'channel':
          cmp = compareText(
            labelFor(settings?.outreachChannels, a.outreachChannelId),
            labelFor(settings?.outreachChannels, b.outreachChannelId),
          );
          break;
        default:
          cmp = 0;
      }
      if (cmp === 0) {
        cmp = compareText(
          a.businessName || a.ownerContact,
          b.businessName || b.ownerContact,
        );
      }
      return cmp * direction;
    });

    return filtered;
  }, [
    prospects,
    search,
    statusFilter,
    sortBy,
    sortDir,
    settings,
    stageOrderById,
    stageLabelById,
  ]);

  const openCreate = () => {
    setEditingProspect(null);
    setProspectModalOpen(true);
  };

  const openEdit = (prospect) => {
    setEditingProspect(prospect);
    setProspectModalOpen(true);
  };

  const openLinkBusiness = (prospect) => {
    setLinkingProspect(prospect);
    setLinkModalOpen(true);
  };

  const handleSaveProspect = async (body, meta = {}) => {
    setSaving(true);
    try {
      let saved;
      if (editingProspect?.id) {
        saved = await outreachCrmService.patchProspect(editingProspect.id, body);
        showToast('success', 'Prospect updated');
      } else {
        saved = await outreachCrmService.createProspect(body);
        showToast('success', 'Prospect created');
      }

      setProspectModalOpen(false);
      setEditingProspect(null);
      await loadData();

      const previousStatusId = meta.previousStatusId || '';
      const shouldPromptLink =
        body.statusId === 'onboarded' &&
        !saved?.businessId &&
        previousStatusId !== 'onboarded';

      if (shouldPromptLink) {
        openLinkBusiness(saved);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleLinkBusiness = async (businessId) => {
    if (!linkingProspect?.id) return;
    setSaving(true);
    try {
      await outreachCrmService.patchProspect(linkingProspect.id, {
        businessId,
        statusId: 'onboarded',
      });
      showToast('success', 'Platform business linked');
      setLinkModalOpen(false);
      setLinkingProspect(null);
      await loadData();
    } finally {
      setSaving(false);
    }
  };

  const handleUnlinkBusiness = async () => {
    if (!linkingProspect?.id) return;
    setSaving(true);
    try {
      await outreachCrmService.patchProspect(linkingProspect.id, {
        businessId: '',
      });
      showToast('success', 'Platform business unlinked');
      setLinkModalOpen(false);
      setLinkingProspect(null);
      await loadData();
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProspect = async () => {
    if (!editingProspect?.id) return;
    if (!window.confirm('Delete this prospect? This cannot be undone.')) return;
    setSaving(true);
    try {
      await outreachCrmService.deleteProspect(editingProspect.id);
      showToast('success', 'Prospect deleted');
      setProspectModalOpen(false);
      setEditingProspect(null);
      await loadData();
    } catch (e) {
      showToast('error', e.message || 'Failed to delete');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSettings = async (body) => {
    setSaving(true);
    try {
      const updated = await outreachCrmService.putSettings(body);
      setSettings(updated);
      setSettingsOpen(false);
      showToast('success', 'CRM settings saved');
      await loadData();
    } finally {
      setSaving(false);
    }
  };

  const activeTrades = (settings?.trades || [])
    .filter((t) => t.active)
    .sort((a, b) => a.sortOrder - b.sortOrder);
  const activeRms = (settings?.relationshipManagers || [])
    .filter((r) => r.active)
    .sort((a, b) => a.sortOrder - b.sortOrder);

  const businessNameById = useMemo(() => {
    const map = new Map();
    businesses.forEach((business) => {
      const id = business?.BusinessId || business?.id;
      if (id) {
        map.set(id, business.BusinessName || id);
      }
    });
    return map;
  }, [businesses]);

  if (loading && !settings) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <NkySpinner />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      <CrmToolbar>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="crm-trade-filter">Trade</InputLabel>
          <Select
            labelId="crm-trade-filter"
            label="Trade"
            value={tradeFilter}
            onChange={(e) => setTradeFilter(e.target.value)}
            sx={{ borderRadius: '10px', background: '#fff' }}
          >
            <MenuItem value="">All trades</MenuItem>
            {activeTrades.map((t) => (
              <MenuItem key={t.id} value={t.id}>
                {t.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="crm-rm-filter">Relationship manager</InputLabel>
          <Select
            labelId="crm-rm-filter"
            label="Relationship manager"
            value={rmFilter}
            onChange={(e) => setRmFilter(e.target.value)}
            sx={{ borderRadius: '10px', background: '#fff' }}
          >
            <MenuItem value="">All managers</MenuItem>
            {activeRms.map((r) => (
              <MenuItem key={r.id} value={r.id}>
                {r.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <CrmField
          size="small"
          label="Search"
          placeholder="Name, contact, city, notes…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 240, flex: 1 }}
        />
        <CrmSecondaryButton
          variant="outlined"
          startIcon={<SettingsOutlinedIcon />}
          onClick={() => setSettingsOpen(true)}
        >
          Settings
        </CrmSecondaryButton>
        <CrmPrimaryButton startIcon={<AddIcon />} onClick={openCreate}>
          Add prospect
        </CrmPrimaryButton>
      </CrmToolbar>

      <CrmKpiRow>
        <CrmKpiCard
          elevation={0}
          role="button"
          tabIndex={0}
          aria-pressed={!statusFilter}
          aria-label="Show all prospects"
          sx={totalKpiCardSx(!statusFilter)}
          onClick={() => setStatusFilter('')}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setStatusFilter('');
            }
          }}
        >
          <CrmKpiValue>{summary?.total ?? 0}</CrmKpiValue>
          <CrmKpiLabel>Total businesses</CrmKpiLabel>
          <CrmKpiPercent>100%</CrmKpiPercent>
        </CrmKpiCard>
        {(summary?.byStatus || []).map((row) => {
          const selected = statusFilter === row.statusId;
          const label = stageLabelById.get(row.statusId) || row.statusId;
          return (
            <CrmKpiCard
              key={row.statusId}
              elevation={0}
              role="button"
              tabIndex={0}
              aria-pressed={selected}
              aria-label={`Filter by ${label}`}
              sx={stageKpiCardSx(row.statusId, selected)}
              onClick={() => handleStatusFilter(row.statusId)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleStatusFilter(row.statusId);
                }
              }}
            >
              <CrmKpiValue className="crm-kpi-value">{row.count}</CrmKpiValue>
              <CrmKpiLabel>{label}</CrmKpiLabel>
              <CrmKpiPercent className="crm-kpi-percent">
                {row.percent}%
              </CrmKpiPercent>
            </CrmKpiCard>
          );
        })}
      </CrmKpiRow>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <NkySpinner />
        </Box>
      ) : (
        <CrmTableWrap>
          <CrmTable>
            <thead>
              <tr>
                {SORT_COLUMNS.map((col) => {
                  const active = sortBy === col.id;
                  const SortIcon = !active
                    ? UnfoldMoreIcon
                    : sortDir === 'asc'
                      ? ArrowUpwardIcon
                      : ArrowDownwardIcon;
                  return (
                    <th
                      key={col.id}
                      scope="col"
                      aria-sort={
                        active
                          ? sortDir === 'asc'
                            ? 'ascending'
                            : 'descending'
                          : 'none'
                      }
                    >
                      <CrmSortHeaderButton
                        type="button"
                        active={active}
                        onClick={() => handleSort(col.id)}
                        aria-label={`Sort by ${col.label}`}
                      >
                        {col.label}
                        <SortIcon className="crm-sort-icon" fontSize="inherit" />
                      </CrmSortHeaderButton>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {filteredProspects.length === 0 ? (
                <tr>
                  <td colSpan={8}>
                    <Typography sx={{ color: '#5a6478', py: 2 }}>
                      No prospects match the current filters.
                    </Typography>
                  </td>
                </tr>
              ) : (
                filteredProspects.map((p) => (
                  <CrmTableRow
                    key={p.id}
                    stageId={p.statusId}
                    onClick={() => openEdit(p)}
                  >
                    <td>
                      <Typography sx={{ fontWeight: 700 }}>
                        {p.businessName || p.ownerContact || '—'}
                      </Typography>
                      {(p.ownerContact || p.city) && (
                        <Typography variant="body2" sx={{ color: '#5a6478' }}>
                          {[p.ownerContact, p.city].filter(Boolean).join(' · ')}
                        </Typography>
                      )}
                      {p.businessId && (
                        <Chip
                          size="small"
                          icon={<LinkIcon />}
                          label={`Platform: ${
                            businessNameById.get(p.businessId) || p.businessId
                          }`}
                          sx={{
                            mt: 0.75,
                            fontWeight: 600,
                            color: '#007d5d',
                            backgroundColor: 'rgba(1, 235, 178, 0.12)',
                          }}
                        />
                      )}
                    </td>
                    <td>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(p.tradeIds || []).map((id) => (
                          <Chip
                            key={id}
                            size="small"
                            label={labelFor(settings?.trades, id)}
                          />
                        ))}
                      </Box>
                    </td>
                    <td>
                      <Chip
                        size="small"
                        label={labelFor(settings?.pipelineStages, p.statusId)}
                        sx={stageChipSx(p.statusId)}
                      />
                    </td>
                    <td>
                      {labelFor(
                        settings?.relationshipManagers,
                        p.relationshipManagerId,
                      )}
                    </td>
                    <td>{p.phone || '—'}</td>
                    <td>{p.email || '—'}</td>
                    <td>{p.lastOutreachDate || '—'}</td>
                    <td>
                      {labelFor(settings?.outreachChannels, p.outreachChannelId)}
                    </td>
                  </CrmTableRow>
                ))
              )}
            </tbody>
          </CrmTable>
        </CrmTableWrap>
      )}

      <AdminCrmProspectModal
        open={prospectModalOpen}
        prospect={editingProspect}
        settings={settings}
        businesses={businesses}
        onClose={() => {
          if (!saving) {
            setProspectModalOpen(false);
            setEditingProspect(null);
          }
        }}
        onSave={handleSaveProspect}
        onDelete={handleDeleteProspect}
        onOpenLinkBusiness={
          editingProspect?.id
            ? () => {
                setProspectModalOpen(false);
                openLinkBusiness(editingProspect);
              }
            : undefined
        }
        saving={saving}
      />

      <AdminCrmLinkBusinessModal
        open={linkModalOpen}
        prospect={linkingProspect}
        businesses={businesses}
        onClose={() => {
          if (!saving) {
            setLinkModalOpen(false);
            setLinkingProspect(null);
          }
        }}
        onLink={handleLinkBusiness}
        onUnlink={handleUnlinkBusiness}
        saving={saving}
      />

      <AdminCrmSettingsModal
        open={settingsOpen}
        settings={settings}
        onClose={() => {
          if (!saving) setSettingsOpen(false);
        }}
        onSave={handleSaveSettings}
        saving={saving}
      />

      <NkySnackbarAlert
        open={toast.open}
        severity={toast.severity}
        message={toast.message}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
      />
    </Box>
  );
}

export default AdminCrm;
